/**
 * Which package each QCS organisation is on.
 *
 * Source of truth is GET /bot/{botId}/package, or `packageDetails` embedded on
 * GET /bot/list. An in-memory cache (and a localStorage fallback for pills
 * before the first fetch) keeps the org list synchronous. CareBrain never
 * calls these helpers for display.
 */
import { loadPackages, tierLevelFor } from './packageTiers'
import { getBotPackage } from '../api/botPackage'

const ORG_PACKAGES_KEY = 'carebrainOrgPackages'

const assignmentCache = {}

export const clearOrgPackageAssignments = () => {
  Object.keys(assignmentCache).forEach(key => {
    delete assignmentCache[key]
  })
  try {
    window.localStorage.removeItem(ORG_PACKAGES_KEY)
  } catch {
    // ignore
  }
}

const readMap = () => {
  try {
    const raw = window.localStorage.getItem(ORG_PACKAGES_KEY)
    return raw ? JSON.parse(raw) || {} : {}
  } catch {
    return {}
  }
}

const writeLocalId = (botId, packageId) => {
  try {
    const map = readMap()
    const key = String(botId)
    if (packageId) map[key] = String(packageId)
    else {
      delete map[key]
      delete map[botId]
    }
    window.localStorage.setItem(ORG_PACKAGES_KEY, JSON.stringify(map))
    return true
  } catch {
    return false
  }
}

export const cacheBotPackageAssignment = (botId, assignment) => {
  if (botId == null) return
  const key = String(botId)
  assignmentCache[key] = assignment || null
  if (assignment?.packageId != null) writeLocalId(botId, assignment.packageId)
}

/**
 * Warm the assignment cache from `packageDetails` on bot/list items so tier
 * pills and usage overlay do not wait on N+1 GET /bot/{id}/package.
 */
export const cachePackageDetailsFromBots = bots => {
  const assignments = {}
  ;(bots || []).forEach(bot => {
    if (bot == null || bot.id == null) return
    // Only act when the list payload includes the field (null = unassigned).
    if (!Object.prototype.hasOwnProperty.call(bot, 'packageDetails')) return
    cacheBotPackageAssignment(bot.id, bot.packageDetails)
    assignments[bot.id] = bot.packageDetails
    assignments[String(bot.id)] = bot.packageDetails
  })
  return assignments
}

export const getCachedBotPackageAssignment = botId => {
  if (botId == null) return null
  const cached = assignmentCache[String(botId)]
  return cached === undefined ? null : cached
}

/** True when GET /bot/list or a prior fetch already wrote this bot into the cache. */
export const hasCachedBotPackageAssignment = botId =>
  botId != null && assignmentCache[String(botId)] !== undefined

export const getOrgPackageId = botId => {
  if (!botId) return ''
  const cached = assignmentCache[String(botId)]
  if (cached && cached.packageId != null) return String(cached.packageId)
  const map = readMap()
  const id = map[botId] || map[String(botId)] || ''
  return id === '' || id == null ? '' : String(id)
}

export const setOrgPackageId = (botId, packageId) => writeLocalId(botId, packageId)

export const getOrgPackageName = botId => {
  const cached = assignmentCache[String(botId)]
  if (cached?.packageName) return cached.packageName
  const id = getOrgPackageId(botId)
  if (!id) return null
  const pkg = loadPackages().find(p => String(p.id) === String(id))
  return pkg ? pkg.name || 'Untitled package' : null
}

/** Basic / Gold / Platinum for the assigned package, or null when unset/empty. */
export const getOrgPackageTierLevel = botId => {
  const id = getOrgPackageId(botId)
  if (!id) return null
  const pkg = loadPackages().find(p => String(p.id) === String(id))
  return pkg ? tierLevelFor(pkg) : null
}

/**
 * Resolve package assignments for bots.
 * - `cacheOnly: true` — return warmed list embeds only; never GET /bot/{id}/package
 *   (org list usage meters).
 * - `skipCached: true` — GET only for ids not already in cache.
 * 404 → no assignment.
 */
export const fetchBotPackageAssignments = (
  botIds,
  signal,
  { skipCached = false, cacheOnly = false } = {},
) => {
  const readCachedOnly = () => {
    const cachedOnly = {}
    ;(botIds || []).forEach(id => {
      if (id == null || id === '') return
      const cached = assignmentCache[String(id)]
      if (cached === undefined) return
      cachedOnly[id] = cached
      cachedOnly[String(id)] = cached
    })
    return cachedOnly
  }

  if (cacheOnly) {
    return Promise.resolve(readCachedOnly())
  }

  const ids = (botIds || []).filter(Boolean).filter(id => {
    if (!skipCached) return true
    return assignmentCache[String(id)] === undefined
  })
  if (ids.length === 0) {
    return Promise.resolve(readCachedOnly())
  }

  return Promise.all(
    ids.map(id =>
      getBotPackage(id, signal)
        .then(data => {
          cacheBotPackageAssignment(id, data)
          return [id, data]
        })
        .catch(() => {
          cacheBotPackageAssignment(id, null)
          return [id, null]
        }),
    ),
  ).then(entries => {
    const fromFetch = entries.reduce((acc, [id, data]) => {
      acc[id] = data
      acc[String(id)] = data
      return acc
    }, {})
    // Include already-cached embeds so overlay still sees every bot on the page.
    ;(botIds || []).forEach(id => {
      if (fromFetch[id] !== undefined || fromFetch[String(id)] !== undefined) return
      const cached = assignmentCache[String(id)]
      if (cached === undefined) return
      fromFetch[id] = cached
      fromFetch[String(id)] = cached
    })
    return fromFetch
  })
}

/** Overlay assignment usageConsumed / limit / reset onto the bulk usage map. */
export const overlayAssignmentOnUsage = (usageByBotId, assignments) => {
  const next = { ...(usageByBotId || {}) }
  Object.keys(assignments || {}).forEach(botId => {
    const assignment = assignments[botId]
    if (!assignment) return
    const existing = next[botId] || next[Number(botId)] || { metrics: {} }
    const metrics = { ...(existing.metrics || {}) }
    const usageLimit = assignment.usageLimit || {}
    const used =
      typeof assignment.usageConsumed === 'number'
        ? assignment.usageConsumed
        : metrics.carePlanAudits?.used || 0

    // Attach used+limit whenever the assignment exposes a cap — not only when
    // `enabled` is true — so exhausted / remaining stay reliable for tooltips.
    const hasLimitConfig =
      usageLimit.enabled ||
      usageLimit.unlimited === true ||
      typeof usageLimit.limit === 'number'

    if (hasLimitConfig) {
      metrics.carePlanAudits = {
        used,
        limit: usageLimit.unlimited ? null : usageLimit.limit,
      }
    } else if (typeof assignment.usageConsumed === 'number') {
      metrics.carePlanAudits = {
        ...(metrics.carePlanAudits || { used: 0 }),
        used: assignment.usageConsumed,
      }
    }
    const merged = {
      ...existing,
      metrics,
      periodEnd: assignment.usageLimitResetAt || existing.periodEnd,
    }
    next[botId] = merged
    next[String(botId)] = merged
  })
  return next
}
